import { observer } from "mobx-react-lite";
import { useCallback } from "react";

import { DateTime } from "@bps/date-time";
import { ScrollablePane } from "@bps/fluent-ui";
import { PagingOptions } from "@libs/api/dtos/index.ts";
import { claimStatusErrors } from "@libs/gateways/acc/AccGateway.dtos.ts";
import { QueryResult } from "@libs/utils/promise-observable/promise-observable.utils.ts";
import { Claim } from "@stores/acc/models/Claim.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { InfiniteScrollList } from "@ui-components/InfiniteScrollList/InfiniteScrollList.tsx";

import { AccErrorLogsFilterValues } from "./AccErrorLogsFilter.types.ts";
import { useAccErrorLogsListColumns } from "./AccErrorLogsList.utils.tsx";

interface AccErrorLogsListProps {
  filter: AccErrorLogsFilterValues;
}

export const AccErrorLogsList = observer(
  ({ filter }: AccErrorLogsListProps) => {
    const columns = useAccErrorLogsListColumns();

    const { acc, practice } = useStores();

    const search = useCallback(
      async (query: PagingOptions): Promise<QueryResult<Claim>> => {
        const result = await acc.fetchClaims({
          ...query,
          ...filter,
          insurers: practice.defaultNzPublicInsurerId
            ? [practice.defaultNzPublicInsurerId]
            : undefined,
          claimStatusCode: filter.claimStatusCode?.length
            ? filter.claimStatusCode
            : claimStatusErrors,
          dateFrom: DateTime.jsDateToISODate(filter.dateFrom),
          dateTo: DateTime.jsDateToISODate(filter.dateTo),
          sortByLastRequested: true
        });

        return result;
      },
      [acc, filter, practice.defaultNzPublicInsurerId]
    );

    return (
      <div style={{ position: "relative", flexGrow: 1 }}>
        <ScrollablePane
          styles={{
            root: {
              height: "100%"
            }
          }}
        >
          <InfiniteScrollList<Claim>
            setKey="claims-list"
            getItems={search}
            columns={columns}
          />
        </ScrollablePane>
      </div>
    );
  }
);
